import { useNavigate } from "react-router-dom";

import { Tag } from "@/components/Tag";

/**
 * 首页（原型 `renderHome`）。
 * DOM 结构与类名逐字保留，仅把 `data-go` 换成路由跳转。
 *
 * 品牌页，无数据依赖；没有对应后端端点，故声明 `static`。
 *
 * @dataSource static
 */
export function HomeView() {
  const navigate = useNavigate();

  return (
    <div className="platform-scroll home-view">
      <section className="brand-hero" aria-labelledby="brand-title">
        <div className="hero-network" aria-hidden="true">
          <svg viewBox="0 0 1440 720" preserveAspectRatio="xMidYMid slice">
            <path
              className="network-path path-muted"
              d="M 716 108 C 820 108, 842 176, 928 176 S 1066 122, 1152 146"
            />
            <path
              className="network-path path-audience"
              d="M 716 108 C 804 108, 820 270, 916 270 S 1066 244, 1164 282"
            />
            <path
              className="network-path path-creator"
              d="M 716 108 C 786 108, 824 386, 936 386 S 1070 438, 1182 428"
            />
            <path
              className="network-path path-muted"
              d="M 928 176 C 1048 176, 1076 548, 1244 548"
            />
            <path
              className="network-path path-audience"
              d="M 916 270 C 1058 270, 1090 548, 1244 548"
            />
            <path
              className="network-path path-creator"
              d="M 936 386 C 1072 386, 1104 548, 1244 548"
            />
            <circle className="network-point point-origin" cx="716" cy="108" r="7" />
            <circle className="network-point point-muted" cx="928" cy="176" r="6" />
            <circle className="network-point point-audience" cx="916" cy="270" r="7" />
            <circle className="network-point point-creator" cx="936" cy="386" r="7" />
            <circle className="network-point point-outcome" cx="1244" cy="548" r="8" />
          </svg>
        </div>

        <div className="brand-hero-copy">
          <span className="hero-kicker">
            <i></i> 互动故事创作与生产平台
          </span>
          <h1 id="brand-title">
            <span>未必</span>
            <small>Otherwise</small>
          </h1>
          <p className="brand-belief">故事不必只有一种可能。</p>
          <p className="brand-manifesto">
            把<span>分支</span>藏起来给所有人，
            <br />把<span>结构</span>展开给创作者。
          </p>
          <p className="brand-support">
            故事玩家只需要做出选择、留下脑洞；专业创作者在背后管理状态、关系、成本与可制作素材。每一次参与，都会成为下一部互动作品的真实信号。
          </p>

          <div className="dual-portals" aria-label="选择进入方式">
            <button
              className="portal portal-audience"
              type="button"
              onClick={() => navigate("/discover")}
            >
              <span className="portal-index">
                <i></i> 故事玩家
              </span>
              <strong>进入故事广场</strong>
              <small>游玩、追更、留下选择与脑洞</small>
              <span className="portal-arrow" aria-hidden="true">
                →
              </span>
            </button>
            <button
              className="portal portal-creator"
              type="button"
              onClick={() => navigate("/studio/overview")}
            >
              <span className="portal-index">
                <i></i> 专业创作者
              </span>
              <strong>进入专业创作台</strong>
              <small>结构、脚本、检查与 AI 预生成</small>
              <span className="portal-arrow" aria-hidden="true">
                →
              </span>
            </button>
          </div>

          <div className="hero-proof" aria-label="平台当前状态">
            <span>
              <strong>1</strong> 个真实项目验证
            </span>
            <span>
              <strong>9</strong> 个固定剧情锚点
            </span>
            <span>
              <strong>286</strong> 条观众信号
            </span>
          </div>
        </div>

        <div className="story-atlas" aria-label="同一个故事的观众视图与创作者视图">
          <div className="atlas-caption">
            <span>同一个故事网络</span>
            <strong>前台是选择，后台是结构</strong>
          </div>
          <div className="atlas-origin">
            <small>故事起点</small>
            <strong>新婚之夜醒来</strong>
            <span>共享剧情锚点 A01</span>
          </div>
          <span className="atlas-line line-one" aria-hidden="true"></span>
          <span className="atlas-line line-two" aria-hidden="true"></span>
          <span className="atlas-line line-three" aria-hidden="true"></span>
          <span className="atlas-line line-four" aria-hidden="true"></span>
          <button
            className="atlas-node node-audience"
            type="button"
            onClick={() => navigate("/discover")}
          >
            <small>故事玩家看到</small>
            <strong>我现在怎么做？</strong>
            <span>自然选择，不必理解结构</span>
          </button>
          <button
            className="atlas-node node-creator"
            type="button"
            onClick={() => navigate("/studio/story")}
          >
            <small>专业创作者看到</small>
            <strong>这个选择改变什么？</strong>
            <span>状态、关系、成本与后果</span>
          </button>
          <div className="atlas-choice choice-one">
            <span>A</span>
            <strong>划开睡处</strong>
            <small>公开边界 · 邻里知情</small>
          </div>
          <div className="atlas-choice choice-two">
            <span>B</span>
            <strong>三日试做</strong>
            <small>生计开启 · 劳动入账</small>
          </div>
          <div className="atlas-choice choice-three">
            <span>C</span>
            <strong>账目换缓冲</strong>
            <small>有限先知 · 信息债 +1</small>
          </div>
          <div className="atlas-outcome">
            <small>重新汇合</small>
            <strong>第二天清晨</strong>
            <span>同一场戏，不同的关系、资源与风险</span>
          </div>
        </div>

        <div className="hero-edge-note">
          <span>PLAY</span>
          <i></i>
          <span>CREATE</span>
          <small>同一套内容网络，两种使用方式</small>
        </div>
      </section>

      <section className="platform-band loop-band" aria-labelledby="loop-title">
        <header className="band-heading">
          <div>
            <span className="eyebrow">同一个内容循环</span>
            <h2 id="loop-title">所有人发现可能，专业创作者完成作品</h2>
          </div>
          <p>
            观众参与不会直接改写正式剧本。它先成为可比较的偏好与脑洞信号，再由专业编剧筛选、验证并写入正式版本。
          </p>
        </header>
        <div className="creation-loop">
          <div className="loop-step">
            <span>01</span>
            <strong>走进故事</strong>
            <small>观众用选择留下真实偏好</small>
          </div>
          <div className="loop-arrow" aria-hidden="true">
            →
          </div>
          <div className="loop-step">
            <span>02</span>
            <strong>提出可能</strong>
            <small>一句脑洞也能进入候选池</small>
          </div>
          <div className="loop-arrow" aria-hidden="true">
            →
          </div>
          <div className="loop-step">
            <span>03</span>
            <strong>专业编排</strong>
            <small>编剧审核并完成互动结构</small>
          </div>
          <div className="loop-arrow" aria-hidden="true">
            →
          </div>
          <div className="loop-step">
            <span>04</span>
            <strong>制作与发布</strong>
            <small>剧本进入图像、视频和交付</small>
          </div>
        </div>
      </section>

      <section className="platform-band home-now" aria-labelledby="home-now-title">
        <header className="band-heading">
          <div>
            <span className="eyebrow">平台此刻</span>
            <h2 id="home-now-title">一边有人走进故事，一边有人把故事做出来</h2>
          </div>
        </header>
        <div className="home-now-grid">
          <article className="now-feature">
            <span className="story-number">01 · 正在共创</span>
            <div>
              <Tag tone="red">互动影游</Tag>
              <h3>金怜盛放</h3>
              <p>玩家扮演潘金怜，从新婚之夜开始重新谈判身体、关系与生计。</p>
            </div>
            <button
              className="button button-accent"
              type="button"
              onClick={() => navigate("/discover")}
            >
              继续游玩
            </button>
          </article>
          <article className="now-signal">
            <span>观众侧</span>
            <strong>286</strong>
            <small>条新脑洞进入候选池</small>
          </article>
          <article className="now-signal">
            <span>创作者侧</span>
            <strong>4</strong>
            <small>个项目正在结构检查</small>
          </article>
          <article className="now-signal">
            <span>生产侧</span>
            <strong>11</strong>
            <small>段视频素材等待审核</small>
          </article>
        </div>
      </section>
    </div>
  );
}
